import { RadioGroup } from '@headlessui/react';
import classnames from 'classnames';
import React from 'react';
import styled from 'styled-components';
import { typography } from '../../theme/typography';
import { style } from '../../utils/css';
import BaseClickable, {
  BaseClickableProps,
} from '../BaseClickable/BaseClickable';
import Stack from '../Stack/Stack';
import { useToggle } from '../../context/ToggleGroupContext';

const Base = styled(BaseClickable)(
  style({
    transition: 'all 75ms',
    bg: 'egg',
    overflow: 'hidden',

    ...typography.body,
    border: 'solid 1px',
    borderColor: 'gray-400',
    borderRadius: 'base',
    ':hover': {
      bg: 'gray-100',
    },
    ':active': {
      borderColor: 'gray-600',
      color: 'gray-600',
    },
    '&.disabled': {
      borderColor: 'gray-300',
      color: 'gray-400',
    },

    ':focus': {
      outline: 'none',
    },

    '&.active': {
      borderColor: 'gray-600',
      boxShadow: 'focus',
      '--focus-offset': '0px',
      '--focus-width': '1px',
    },

    '&.checked': {
      bg: 'gray-200',
      borderColor: 'gray-600',

      '&:hover': {
        bg: 'gray-200',
      },

      '&:active': {
        bg: 'gray-100',
      },
    },
  }),
);

export type ToggleButtonProps = {
  value: string;
} & BaseClickableProps;

const ToggleButton = ({
  value,
  disabled: forceDisabled,
  ...props
}: ToggleButtonProps) => {
  const { dense, disabled: stateDisabled } = useToggle();
  const disabled = forceDisabled || stateDisabled;

  return (
    <RadioGroup.Option
      className={({ active, checked }) =>
        classnames({ active, checked, disabled })
      }
      as={Base}
      value={value}
      disabled={disabled}
      disableDefaultFocusStyles
      type="button"
      {...props}
    >
      {({ checked, active }) => (
        <Stack
          className={classnames({ selected: checked, active })}
          sx={{
            px: dense ? 2 : 3,
            py: dense ? 1.5 : 2,
          }}
          {...props}
        />
      )}
    </RadioGroup.Option>
  );
};

ToggleButton.displayName = 'ToggleButton';
export default ToggleButton;
